import { listBacktests, getBacktest } from "../../../services/backtestsApi";
import { getReport, listReportStrategyPolicyVersions } from "../../../services/reportsApi";
import {
  getWorkspace,
  registerWorkspaceNode,
  WorkspacesApiError,
} from "../../../services/workspacesApi";
import type { Backtest } from "../../../types/backtests";
import type { ReportStrategyPolicyVersion } from "../../../types/reports";
import type { WorkspaceAccountScope } from "../../../types/workspaceAccountScope";
import type {
  WorkspaceDetail,
  WorkspaceNode,
  WorkspaceNodeMeta,
  WorkspaceNodePosition,
} from "../../../types/workspace";
import { isPersistedWorkspaceResourceId } from "../../../utils/workspacePersistIds";
import { isPolicyBundleVersion } from "../../../utils/reportStrategyPineUi";
import {
  extractCombinedSourceRefsFromPolicyTree,
  resolveCombinedSourceVersions,
} from "../Strategies/strategyDetailUtils";
import { resolveStrategyIdForBacktest } from "../ExecutionRulesSimulator/simulatorPolicyVersionSaved";
import { computeChildNodePosition, computeMultiParentMergePosition } from "./workspaceLayoutUtils";
import { findUpstreamStrategyEntityId } from "./workspaceGraphUtils";
import {
  isOptimisticWorkspaceNodeId,
  workspacePersistNodeId,
} from "./workspaceOptimisticReport";

/** Matches backend `MAX_SIMULATIONS_PER_REPORT`. */
export const WORKSPACE_MAX_SIMULATIONS_PER_REPORT = 16;

/** Count simulation children under a report node (canvas graph). */
export function countWorkspaceSimulationsForReport(
  reportNodeId: string,
  nodes: WorkspaceNode[],
  edges: Array<{ sourceNodeId: string; targetNodeId: string }>
): number {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  let count = 0;
  for (const edge of edges) {
    if (edge.sourceNodeId !== reportNodeId) continue;
    const child = byId.get(edge.targetNodeId);
    if (child?.type === "simulation") count += 1;
  }
  return count;
}

export function isWorkspaceSimulationCapReached(
  reportNodeId: string,
  nodes: WorkspaceNode[],
  edges: Array<{ sourceNodeId: string; targetNodeId: string }>,
  max = WORKSPACE_MAX_SIMULATIONS_PER_REPORT
): boolean {
  return countWorkspaceSimulationsForReport(reportNodeId, nodes, edges) >= max;
}

export function workspaceSimulationCapReachedCopy(max: number): {
  title: string;
  description: string;
} {
  return {
    title: "Simulation limit reached",
    description: `This report already has ${max} simulation${max === 1 ? "" : "s"} on the canvas. Delete one to run another.`,
  };
}

/** Keep under the global HTTP rate limit (100/min) during lineage attach. */
const REGISTER_CONCURRENCY = 3;

export type WorkspaceLineageHydrateResult = {
  simulationsAdded: number;
  policiesAdded: number;
  combinedPoliciesAdded?: number;
  combinedSimulationsAdded?: number;
};

function nodeKey(type: WorkspaceNode["type"], entityId: string): string {
  return `${type}:${entityId.trim()}`;
}

function buildNodeIndex(detail: WorkspaceDetail): Map<string, WorkspaceNode> {
  const map = new Map<string, WorkspaceNode>();
  for (const node of detail.nodes) {
    map.set(nodeKey(node.type, node.entityId), node);
  }
  return map;
}

function sortBacktestsNewestFirst(rows: Backtest[]): Backtest[] {
  return [...rows].sort((a, b) => {
    const aTime = Date.parse(String(a.created_at ?? a.updated_at ?? "")) || 0;
    const bTime = Date.parse(String(b.created_at ?? b.updated_at ?? "")) || 0;
    return bTime - aTime;
  });
}

function collectStrategyIdsForPolicies(
  reportStrategyId: string | null | undefined,
  backtests: Backtest[]
): string[] {
  const ids = new Set<string>();
  const fromReport = reportStrategyId?.trim();
  if (fromReport) ids.add(fromReport);
  for (const row of backtests) {
    const sid = row.strategy_id?.trim();
    if (sid) ids.add(sid);
  }
  return Array.from(ids);
}

function isBundleBacktest(backtest: Backtest): boolean {
  if (backtest.policy_schema_version === "policy_tree.bundle.v1") return true;
  const tree = backtest.policy_tree as { schema_version?: string } | null | undefined;
  return tree?.schema_version === "policy_tree.bundle.v1";
}

function sourceIdSetKey(ids: string[]): string {
  return [...ids].map((id) => id.trim()).filter(Boolean).sort().join("|");
}

function sourceIdsFromBundleTree(tree: unknown): string[] {
  const refs = extractCombinedSourceRefsFromPolicyTree(tree);
  return Array.from(new Set(refs.versionIds.map((id) => id.trim()).filter(Boolean)));
}

/**
 * One policy canvas node per simulation: when several versions cite the same
 * `source_backtest_id`, keep a single preferred version (current → newest number).
 */
export function pickPreferredPolicyVersionForSimulation(
  versions: ReportStrategyPolicyVersion[],
  backtestId: string
): ReportStrategyPolicyVersion | null {
  const id = backtestId.trim();
  if (!id) return null;
  const matching = versions.filter(
    (version) =>
      String(version.source_backtest_id ?? "").trim() === id && !isPolicyBundleVersion(version)
  );
  if (matching.length === 0) return null;
  if (matching.length === 1) return matching[0]!;

  const rank = (version: ReportStrategyPolicyVersion): number[] => {
    const archived = version.is_archived === true ? 0 : 1;
    const current = version.is_current === true ? 1 : 0;
    const versionNumber =
      typeof version.version_number === "number" && Number.isFinite(version.version_number)
        ? version.version_number
        : -1;
    const createdAt = Date.parse(String(version.created_at ?? "")) || 0;
    return [archived, current, versionNumber, createdAt];
  };

  return [...matching].sort((a, b) => {
    const ra = rank(a);
    const rb = rank(b);
    for (let i = 0; i < ra.length; i += 1) {
      if (rb[i]! !== ra[i]!) return rb[i]! - ra[i]!;
    }
    return String(b.id).localeCompare(String(a.id));
  })[0]!;
}

async function loadPolicyVersionsForStrategies(
  strategyIds: string[]
): Promise<ReportStrategyPolicyVersion[]> {
  const byId = new Map<string, ReportStrategyPolicyVersion>();
  // Sequential to avoid bursting the global rate limit when many strategies exist.
  for (const strategyId of strategyIds) {
    try {
      const response = await listReportStrategyPolicyVersions(strategyId, {
        include_archived: true,
      });
      for (const version of response.versions ?? []) {
        const id = String(version.id ?? "").trim();
        if (id) byId.set(id, version);
      }
    } catch {
      // Strategy may be missing or inaccessible — skip without failing the attach.
    }
  }
  return Array.from(byId.values());
}

async function mapPool<T>(
  items: T[],
  concurrency: number,
  worker: (item: T) => Promise<void>
): Promise<void> {
  if (items.length === 0) return;
  const limit = Math.max(1, concurrency);
  let index = 0;

  async function runNext(): Promise<void> {
    while (index < items.length) {
      const current = index;
      index += 1;
      await worker(items[current]!);
    }
  }

  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, () => runNext()));
}

async function registerMissingNode(args: {
  scope: WorkspaceAccountScope;
  workspaceId: string;
  type: WorkspaceNode["type"];
  entityId: string;
  parentNodeId?: string;
  parentNodeIds?: string[];
  position?: WorkspaceNodePosition;
  meta?: WorkspaceNodeMeta;
  index: Map<string, WorkspaceNode>;
}): Promise<{ node: WorkspaceNode; created: boolean } | null> {
  const key = nodeKey(args.type, args.entityId);
  const existing = args.index.get(key);
  if (existing) return { node: existing, created: false };

  const parentNodeIds = args.parentNodeIds?.filter(Boolean);
  const parentNodeId = args.parentNodeId ?? parentNodeIds?.[0];
  if (!parentNodeId) return null;

  try {
    const registered = await registerWorkspaceNode(args.scope, args.workspaceId, {
      type: args.type,
      entityId: args.entityId,
      parentNodeId,
      parentNodeIds: parentNodeIds && parentNodeIds.length > 1 ? parentNodeIds : undefined,
      position: args.position,
      meta: args.meta,
    });
    args.index.set(key, registered.node);
    return { node: registered.node, created: true };
  } catch (err) {
    if (err instanceof WorkspacesApiError && err.slug === "workspace.duplicate_node") {
      // Another tab/request won the race — don't GET the full workspace again.
      return null;
    }
    if (err instanceof WorkspacesApiError && err.slug === "workspace.simulation_limit_reached") {
      throw err;
    }
    // Soft-fail individual node registration so one bad entity doesn't abort the rest.
    console.warn("Workspace lineage register failed", args.type, args.entityId, err);
    return null;
  }
}

/** Register policy nodes under a simulation from versions that cite its backtest. */
export async function hydrateWorkspaceSimulationPolicies(args: {
  scope: WorkspaceAccountScope;
  workspaceId: string;
  simulationNode: WorkspaceNode;
  backtestId: string;
  strategyIds?: string[];
  knownVersions?: ReportStrategyPolicyVersion[];
  detail?: WorkspaceDetail | null;
}): Promise<number> {
  const backtestId = args.backtestId.trim();
  if (
    !backtestId ||
    (args.simulationNode.type !== "simulation" &&
      args.simulationNode.type !== "combined_simulation")
  ) {
    return 0;
  }

  const parentNodeId = workspacePersistNodeId(args.simulationNode);
  if (!isPersistedWorkspaceResourceId(parentNodeId)) return 0;

  let versions = args.knownVersions;
  if (!versions) {
    let strategyIds = [...(args.strategyIds ?? [])].map((id) => id.trim()).filter(Boolean);
    if (strategyIds.length === 0) {
      // Canvas may omit the strategy node even when the backtest/report is linked.
      try {
        const backtest = await getBacktest(backtestId, { include_result: false });
        const resolved = await resolveStrategyIdForBacktest(backtest);
        if (resolved) strategyIds = [resolved];
      } catch {
        // Fall through — no versions to match.
      }
    }
    if (strategyIds.length === 0) return 0;
    versions = await loadPolicyVersionsForStrategies(strategyIds);
  }

  // Single-policy only — bundles become combined_policy nodes separately.
  // One canvas policy child per simulation (prefer current / newest version).
  const preferred = pickPreferredPolicyVersionForSimulation(versions, backtestId);
  if (!preferred) return 0;
  const entityId = String(preferred.id ?? "").trim();
  if (!entityId) return 0;

  const detail =
    args.detail ?? (await getWorkspace(args.scope, args.workspaceId));
  const index = buildNodeIndex(detail);
  // Ensure parent is present in the index for sibling placement.
  index.set(nodeKey("simulation", args.simulationNode.entityId), args.simulationNode);

  const position = computeChildNodePosition(args.simulationNode, []);
  const result = await registerMissingNode({
    scope: args.scope,
    workspaceId: args.workspaceId,
    type: "policy",
    entityId,
    parentNodeId,
    position,
    index,
  });
  return result?.created ? 1 : 0;
}

/**
 * For every simulation on the canvas, register the preferred policy version sourced
 * from that backtest (at most one POLICY child per simulation).
 */
export async function hydrateWorkspaceAllSimulationPolicies(args: {
  scope: WorkspaceAccountScope;
  workspaceId: string;
  detail: WorkspaceDetail;
}): Promise<number> {
  const sims = args.detail.nodes.filter(
    (node) =>
      (node.type === "simulation" || node.type === "combined_simulation") &&
      !isOptimisticWorkspaceNodeId(node.id) &&
      isPersistedWorkspaceResourceId(workspacePersistNodeId(node)) &&
      node.entityId.trim()
  );
  if (sims.length === 0) return 0;

  const strategyIds = new Set<string>();
  for (const sim of sims) {
    const fromGraph = findUpstreamStrategyEntityId(sim.id, args.detail.nodes, args.detail.edges);
    if (fromGraph) strategyIds.add(fromGraph);
  }

  // Sims without a strategy node on the canvas still have versions on the linked strategy.
  await mapPool(sims, REGISTER_CONCURRENCY, async (sim) => {
    const fromGraph = findUpstreamStrategyEntityId(sim.id, args.detail.nodes, args.detail.edges);
    if (fromGraph) return;
    try {
      const backtest = await getBacktest(sim.entityId, { include_result: false });
      const resolved = await resolveStrategyIdForBacktest(backtest);
      if (resolved) strategyIds.add(resolved);
    } catch {
      // ignore
    }
  });

  if (strategyIds.size === 0) return 0;
  const versions = await loadPolicyVersionsForStrategies(Array.from(strategyIds));
  if (versions.length === 0) return 0;

  let policiesAdded = 0;
  // Fresh detail snapshot for the shared index; register updates mutate the local index only.
  let detail = args.detail;
  for (const sim of sims) {
    const added = await hydrateWorkspaceSimulationPolicies({
      scope: args.scope,
      workspaceId: args.workspaceId,
      simulationNode: sim,
      backtestId: sim.entityId,
      knownVersions: versions,
      detail,
    });
    if (added > 0) {
      policiesAdded += added;
      // Keep parent/sibling placement accurate after new nodes land.
      detail = await getWorkspace(args.scope, args.workspaceId);
    }
  }
  return policiesAdded;
}

async function hydrateCombinedPoliciesAndSims(args: {
  scope: WorkspaceAccountScope;
  workspaceId: string;
  reportId: string;
  versions: ReportStrategyPolicyVersion[];
  backtests: Backtest[];
  index: Map<string, WorkspaceNode>;
}): Promise<{ combinedPoliciesAdded: number; combinedSimulationsAdded: number }> {
  let combinedPoliciesAdded = 0;
  let combinedSimulationsAdded = 0;

  const bundleVersions = args.versions.filter(isPolicyBundleVersion);
  const combinedBySourceKey = new Map<string, WorkspaceNode>();

  // Seed with any combined policies already on the canvas.
  for (const node of Array.from(args.index.values())) {
    if (node.type !== "combined_policy") continue;
    const metaIds = node.meta?.sourcePolicyVersionIds;
    if (metaIds && metaIds.length >= 2) {
      combinedBySourceKey.set(sourceIdSetKey(metaIds), node);
      continue;
    }
    const version = args.versions.find((row) => String(row.id) === node.entityId);
    if (!version) continue;
    const sources = resolveCombinedSourceVersions(version, args.versions);
    if (sources && sources.length >= 2) {
      combinedBySourceKey.set(
        sourceIdSetKey(sources.map((source) => source.id)),
        node
      );
    }
  }

  for (const version of bundleVersions) {
    const entityId = String(version.id ?? "").trim();
    if (!entityId) continue;

    const sources = resolveCombinedSourceVersions(version, args.versions);
    if (!sources || sources.length < 2) continue;

    const parentNodes = sources
      .map((source) => args.index.get(nodeKey("policy", source.id)))
      .filter((node): node is WorkspaceNode => Boolean(node));
    if (parentNodes.length < 2) continue;

    const parentNodeIds = parentNodes.map((node) => workspacePersistNodeId(node));
    const sourceKey = sourceIdSetKey(sources.map((source) => source.id));
    const existing = args.index.get(nodeKey("combined_policy", entityId));
    if (existing) {
      combinedBySourceKey.set(sourceKey, existing);
      continue;
    }

    const position = computeMultiParentMergePosition(parentNodes);
    const result = await registerMissingNode({
      scope: args.scope,
      workspaceId: args.workspaceId,
      type: "combined_policy",
      entityId,
      parentNodeIds,
      position,
      meta: {
        title: version.title?.trim() || version.source_backtest_title?.trim() || "Combined policy",
        adoptedReportId: args.reportId,
        sourcePolicyVersionIds: sources.map((source) => source.id),
      },
      index: args.index,
    });
    if (result?.created) combinedPoliciesAdded += 1;
    if (result?.node) combinedBySourceKey.set(sourceKey, result.node);
  }

  const bundleBacktests = args.backtests.filter(isBundleBacktest);
  let siblingIndex = 0;
  for (const backtest of bundleBacktests) {
    const backtestId = String(backtest.id ?? "").trim();
    if (!backtestId) continue;

    const sourceIds = sourceIdsFromBundleTree(backtest.policy_tree);
    const sourceKey = sourceIds.length >= 2 ? sourceIdSetKey(sourceIds) : "";
    let parent =
      (sourceKey ? combinedBySourceKey.get(sourceKey) : undefined) ??
      Array.from(args.index.values()).find((node) => {
        if (node.type !== "combined_policy") return false;
        const version = args.versions.find((row) => String(row.id) === node.entityId);
        if (!version) return false;
        // Prefer exact source-set match; otherwise if only one combined policy exists, use it.
        const sources = resolveCombinedSourceVersions(version, args.versions);
        if (!sources || sourceIds.length < 2) return combinedBySourceKey.size === 1;
        return sourceIdSetKey(sources.map((s) => s.id)) === sourceKey;
      });

    if (!parent && combinedBySourceKey.size === 1) {
      parent = Array.from(combinedBySourceKey.values())[0];
    }
    if (!parent || parent.type !== "combined_policy") continue;

    const position = computeChildNodePosition(parent, [
      ...Array.from({ length: siblingIndex }, (_, i) => ({
        id: `tmp-cs-${i}`,
        sourceNodeId: parent!.id,
        targetNodeId: `tmp-cs-target-${i}`,
      })),
    ]);
    siblingIndex += 1;

    const result = await registerMissingNode({
      scope: args.scope,
      workspaceId: args.workspaceId,
      type: "combined_simulation",
      entityId: backtestId,
      parentNodeId: workspacePersistNodeId(parent),
      position,
      index: args.index,
    });
    if (result?.created) combinedSimulationsAdded += 1;
  }

  return { combinedPoliciesAdded, combinedSimulationsAdded };
}

/**
 * After attaching an existing report, pull in its saved simulations and any
 * policy versions derived from those simulations (via source_backtest_id).
 * Bundle policies/backtests hydrate as combined_policy / combined_simulation.
 */
export async function hydrateWorkspaceReportLineage(args: {
  scope: WorkspaceAccountScope;
  workspaceId: string;
  reportNode: WorkspaceNode;
  reportEntityId: string;
  maxSimulationsPerReport?: number;
}): Promise<WorkspaceLineageHydrateResult> {
  const reportId = args.reportEntityId.trim();
  if (!reportId || args.reportNode.type !== "report") {
    return { simulationsAdded: 0, policiesAdded: 0 };
  }
  const simCap = args.maxSimulationsPerReport ?? WORKSPACE_MAX_SIMULATIONS_PER_REPORT;

  let reportStrategyId: string | null = null;
  try {
    const report = await getReport(reportId);
    reportStrategyId =
      typeof report.strategy_id === "string" ? report.strategy_id.trim() || null : null;
  } catch {
    reportStrategyId = null;
  }

  const listed = await listBacktests({
    report_id: reportId,
    limit: simCap,
    include_result: false,
    include_snapshot: false,
  });
  const backtests = sortBacktestsNewestFirst(listed.backtests ?? []).slice(0, simCap);

  if (backtests.length === 0) {
    return { simulationsAdded: 0, policiesAdded: 0 };
  }

  const strategyIds = collectStrategyIdsForPolicies(reportStrategyId, backtests);
  const versions = await loadPolicyVersionsForStrategies(strategyIds);

  // One workspace snapshot — skip anything already on the canvas without N extra GETs.
  const detail = await getWorkspace(args.scope, args.workspaceId);
  const index = buildNodeIndex(detail);
  index.set(nodeKey("report", args.reportNode.entityId), args.reportNode);

  let simulationsAdded = 0;
  let policiesAdded = 0;
  let hitSimLimit = false;

  type SimJob = {
    backtest: Backtest;
    backtestId: string;
    siblingIndex: number;
  };

  const simJobs: SimJob[] = [];
  for (let i = 0; i < backtests.length; i += 1) {
    const backtest = backtests[i]!;
    if (isBundleBacktest(backtest)) continue;
    const backtestId = String(backtest.id ?? "").trim();
    if (!backtestId) continue;
    simJobs.push({ backtest, backtestId, siblingIndex: simJobs.length });
  }

  const registeredSims: Array<{ node: WorkspaceNode; backtestId: string; strategyId?: string }> =
    [];

  await mapPool(simJobs, REGISTER_CONCURRENCY, async (job) => {
    if (hitSimLimit) return;

    const position = computeChildNodePosition(args.reportNode, [
      ...Array.from({ length: job.siblingIndex }, (_, index) => ({
        id: `tmp-sim-${index}`,
        sourceNodeId: args.reportNode.id,
        targetNodeId: `tmp-sim-target-${index}`,
      })),
    ]);

    try {
      const result = await registerMissingNode({
        scope: args.scope,
        workspaceId: args.workspaceId,
        type: "simulation",
        entityId: job.backtestId,
        parentNodeId: workspacePersistNodeId(args.reportNode),
        position,
        index,
      });
      if (!result) return;
      if (result.created) simulationsAdded += 1;
      registeredSims.push({
        node: result.node,
        backtestId: job.backtestId,
        strategyId: job.backtest.strategy_id?.trim() || undefined,
      });
    } catch (err) {
      if (err instanceof WorkspacesApiError && err.slug === "workspace.simulation_limit_reached") {
        hitSimLimit = true;
      }
    }
  });

  // Policies after sims exist (and index is warm). Sequential per sim, limited concurrency across sims.
  await mapPool(registeredSims, REGISTER_CONCURRENCY, async (sim) => {
    const simStrategyIds = Array.from(
      new Set([...strategyIds, ...(sim.strategyId ? [sim.strategyId] : [])])
    );
    const added = await hydrateWorkspaceSimulationPolicies({
      scope: args.scope,
      workspaceId: args.workspaceId,
      simulationNode: sim.node,
      backtestId: sim.backtestId,
      strategyIds: simStrategyIds,
      knownVersions: versions,
      detail: {
        ...detail,
        nodes: Array.from(index.values()),
      },
    });
    policiesAdded += added;
  });

  const combined = await hydrateCombinedPoliciesAndSims({
    scope: args.scope,
    workspaceId: args.workspaceId,
    reportId,
    versions,
    backtests,
    index,
  });

  return {
    simulationsAdded,
    policiesAdded,
    combinedPoliciesAdded: combined.combinedPoliciesAdded,
    combinedSimulationsAdded: combined.combinedSimulationsAdded,
  };
}
